import { PractitionersSearchResult } from '~/api';
import { trackEvent } from '../useAnalytics';
import {
  ErrorLevel,
  formatDate,
  logErrorToSentry,
  OpeningBadgeStatus,
} from '~/utilities';

interface MainJaneOpening {
  staff_member_id: number;
  location_id: number;
  duration: number;
  start_at: string;
  end_at: string;
  status: string;
}

interface MainJaneShift {
  start_at: string;
  end_at: string;
  call_to_book: boolean;
}

interface MainJaneOpeningsResponse {
  id: string;
  full_name: string;
  first_date: string;
  openings: MainJaneOpening[];
  shifts: MainJaneShift[];
}

export const trackAvailabilityStaleness = async (
  practitionerSearchResult: PractitionersSearchResult,
  openingBadgeStatus?: OpeningBadgeStatus
) => {
  const clinicDomain = getClinicDomain(practitionerSearchResult);
  if (!clinicDomain) {
    logErrorToSentry({
      type: ErrorLevel.Warning,
      message: `Could not get the clinic domain, practitioner ${practitionerSearchResult.marketplacePractitionerLocationId} was missing clinicBookingUrl. Will not report availability staleness.`,
    });
    return;
  }

  if (!practitionerSearchResult.defaultTreatmentId) {
    logErrorToSentry({
      type: ErrorLevel.Warning,
      message: `Could not get the clinic domain, practitioner ${practitionerSearchResult.marketplacePractitionerLocationId} was missing defaultTreatmentId. Will not report availability staleness.`,
    });
    return;
  }

  // Call `mainjane:ListOpenings` for the practitioner location
  listMainJaneOpenings(
    clinicDomain,
    getStaffMemberId(practitionerSearchResult),
    getLocationId(practitionerSearchResult),
    practitionerSearchResult.defaultTreatmentId!,
    7
  ).then((mainJaneResponse) => {
    if (!mainJaneResponse) {
      logErrorToSentry({
        type: ErrorLevel.Warning,
        message: 'MainJane:ListOpenings returned no body.',
      });
      return;
    }

    const mainJaneOpeningDates = mainJaneResponse[0].openings.map(
      (opening) => opening.start_at
    );

    const mainJaneOpeningBadgeStatuses = mainJaneOpeningDates
      .map(formatDate) // Convert to OpeningBadgeStatus (or undefined)
      .filter((opening) => {
        if (opening) {
          return true;
        } else {
          return false;
        }
      }); // Remove undefined -- they're >7d in future.

    if (!openingBadgeStatus && mainJaneOpeningBadgeStatuses.length > 0) {
      // We have no badge, but MainJane has openings for the clinic within a week.
      // We've therefore not shown a badge when we should have. The badge is missing.
      trackEvent('mpl_opening_badge_missing', {
        mplOpeningBadgeStatus: openingBadgeStatus,
        mplFirstOpening: practitionerSearchResult.firstOpening
          ? practitionerSearchResult.firstOpening.startAt
          : 'undefined',
        mainJaneFirstOpening: mainJaneOpeningDates[0],
      });
    } else if (!openingBadgeStatus) {
      // We do not have a badge and there are no openings. We have correctly shown no badge.
      trackEvent('mpl_opening_badge_correct_not_shown', {});
    } else if (mainJaneOpeningBadgeStatuses.includes(openingBadgeStatus)) {
      // We have a badge and it's in the list of MainJane's openings. We have correctly shown a badge.
      trackEvent('mpl_opening_badge_correct_shown', {});
    } else if (!mainJaneOpeningBadgeStatuses.includes(openingBadgeStatus)) {
      // We have a badge but MainJane thinks the clinic has no openings.
      // We've therefore wrongly shown a badge and should log an incorrect badge.
      trackEvent('mpl_opening_badge_incorrect', {
        mplOpeningBadgeStatus: openingBadgeStatus,
        mplFirstOpening: practitionerSearchResult.firstOpening
          ? practitionerSearchResult.firstOpening.startAt
          : 'undefined',
        mainJaneFirstOpening: mainJaneOpeningDates[0],
      });
    }
  });
};

const listMainJaneOpenings = async (
  domain: string,
  staffMemberId: string,
  locationId: string,
  treatmentId: number,
  numDays: number
) => {
  try {
    const response = await fetch(
      `https://${domain}/api/v2/openings?location_id=${locationId}&staff_member_id=${staffMemberId}&treatment_id=${treatmentId}&num_days=${numDays}`,
      {
        method: 'GET',
      }
    );
    return (await response.json()) as MainJaneOpeningsResponse[];
  } catch (error) {
    logErrorToSentry({
      type: ErrorLevel.Warning,
      message: `Could not load MainJane openings to send availability staleness metric to Rudderstack. ${error}`,
    });
  }
};

const getStaffMemberId = (
  practitionerSearchResult: PractitionersSearchResult
) => {
  return practitionerSearchResult.staffMemberGuid.split('-')[1];
};

const getClinicDomain = (
  practitionerSearchResult: PractitionersSearchResult
) => {
  if (!practitionerSearchResult.clinicBookingUrl) {
    return;
  }
  return new URL(practitionerSearchResult.clinicBookingUrl).host;
};

const getLocationId = (practitionerSearchResult: PractitionersSearchResult) => {
  return practitionerSearchResult.clinicLocationGuid.split('-')[1];
};
